import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MapPin, Linkedin, Facebook, ArrowUp, ChevronLeft, ChevronRight, Calendar, Clock, CheckCircle } from "lucide-react";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { COOKIE_SETTINGS_EVENT } from "@/components/common/CookieConsent";

export function Footer() {
  const { t } = useTranslation('common');
  const { isRTL } = useLanguage();
  
  const footerLinks = {
    services: [
      { name: t('footerLinks.safetyTrainings'), href: "/trainings" },
      { name: t('footerLinks.riskAssessments'), href: "/services#risk-assessment" },
      { name: t('footerLinks.consulting'), href: "/consulting" },
      { name: t('footerLinks.constructionSafety'), href: "/services#construction" },
      { name: t('footerLinks.digitalization'), href: "/services#digital" },
    ],
    industries: [
      { name: t('footerLinks.constructionInfra'), href: "/industries#construction" },
      { name: t('footerLinks.manufacturing'), href: "/industries#manufacturing" },
      { name: t('footerLinks.education'), href: "/industries#education" },
    ],
    resources: [
      { name: t('footerLinks.blog'), href: "/blog" },
      { name: t('footerLinks.library'), href: "/library" },
      { name: t('footer.privacy'), href: "/privacy" },
      { name: t('footer.terms'), href: "/terms" },
    ],
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const ArrowIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <footer className="bg-primary text-primary-foreground relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-accent/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-accent/5 rounded-full blur-[100px]" />
      
      {/* Decorative Top Border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-l from-accent via-accent/50 to-accent" />
      
      {/* Book a Call Section */}
      <div className="border-b border-primary-foreground/10 relative z-10">
        <div className="container mx-auto py-12 md:py-16 px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center max-w-6xl mx-auto">
            {/* Left: Content */}
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 mb-6">
                <Calendar className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground/90 text-sm font-semibold">{t('footer.bookCall.badge')}</span>
              </span>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
                {t('footer.bookCall.title')}
              </h3>
              <p className="text-primary-foreground/70 leading-relaxed mb-6">
                {t('footer.bookCall.description')}
              </p>
              
              {/* Quick Info */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2 text-primary-foreground/80">
                  <Clock className="w-4 h-4 text-accent" />
                  <span className="text-sm font-medium">{t('footer.bookCall.duration')}</span>
                </div>
                <div className="flex items-center gap-2 text-primary-foreground/80">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span className="text-sm font-medium">{t('footer.bookCall.method')}</span>
                </div>
              </div>

              <p className="text-xs text-primary-foreground/50 mb-6">
                {t('footer.bookCall.forWhom')}
              </p>

              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2">
                  <Calendar className="w-4 h-4" />
                  {t('buttons.bookCall')}
                  <ArrowIcon className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Right: Calendar Embed */}
            <div>
              <div className="bg-primary-foreground/5 rounded-2xl border border-primary-foreground/10 p-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-accent/20 flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-7 h-7 text-accent" />
                </div>
                <h4 className="font-heading font-bold text-lg text-primary-foreground mb-2">
                  {t('footer.bookCall.calendarTitle')}
                </h4>
                <p className="text-primary-foreground/60 text-sm mb-4">
                  {t('footer.bookCall.calendarDescription')}
                </p>
                <Link to="/contact">
                  <Button variant="heroOutline" size="sm" className="gap-2">
                    {t('buttons.toSchedule')}
                    <ArrowIcon className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Footer */}
      <div className="container mx-auto py-12 md:py-16 lg:py-20 px-4 md:px-8 relative z-10">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-8 lg:gap-8">
          {/* Brand Column */}
          <div className="col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-4">
            <Logo variant="light" size="md" className="mb-4 md:mb-6" />
            <p className="text-primary-foreground/60 text-xs sm:text-sm leading-relaxed mb-6 md:mb-8 max-w-sm">
              {t('footer.brandDescription')}
            </p>
            
            {/* Social */}
            <div className="flex gap-3">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-xl bg-primary-foreground/10 flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-all duration-300 hover:scale-105"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-xl bg-primary-foreground/10 flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-all duration-300 hover:scale-105"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div className="col-span-1 lg:col-span-2">
            <h3 className="font-heading font-bold text-sm sm:text-base md:text-lg mb-4 md:mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent" />
              {t('footer.services')}
            </h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/60 hover:text-accent transition-colors duration-300 text-sm font-medium"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries Column */}
          <div className="col-span-1 lg:col-span-2">
            <h3 className="font-heading font-bold text-sm sm:text-base md:text-lg mb-4 md:mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent" />
              {t('footer.industries')}
            </h3>
            <ul className="space-y-3">
              {footerLinks.industries.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/60 hover:text-accent transition-colors duration-300 text-sm font-medium"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div className="col-span-1 lg:col-span-2">
            <h3 className="font-heading font-bold text-sm sm:text-base md:text-lg mb-4 md:mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent" />
              {t('footer.resources')}
            </h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/60 hover:text-accent transition-colors duration-300 text-sm font-medium"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="col-span-1 lg:col-span-2">
            <h3 className="font-heading font-bold text-sm sm:text-base md:text-lg mb-4 md:mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent" />
              {t('footer.contact')}
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+972528300088"
                  className="flex items-center gap-4 text-primary-foreground/70 hover:text-accent transition-colors duration-300 group"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary-foreground/10 flex items-center justify-center group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-300">
                    <Phone className="w-5 h-5" />
                  </div>
                  <span dir="ltr" className="text-sm font-medium">052-830-0088</span>
                </a>
              </li>
              <li>
              <a
                  href="mailto:Support@Adam-Safety.com"
                  className="flex items-center gap-4 text-primary-foreground/70 hover:text-accent transition-colors duration-300 group"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary-foreground/10 flex items-center justify-center group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-300">
                    <Mail className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium">Support@Adam-Safety.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-center gap-4 text-primary-foreground/70">
                  <div className="w-11 h-11 rounded-xl bg-primary-foreground/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium">{t('location')}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10 relative z-10"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="container mx-auto py-4 md:py-6 px-4 md:px-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-primary-foreground/40 text-xs sm:text-sm text-center md:text-right">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-primary-foreground/40 hover:text-accent transition-colors duration-300 text-sm">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms" className="text-primary-foreground/40 hover:text-accent transition-colors duration-300 text-sm">
              {t('footer.terms')}
            </Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT))}
              className="text-primary-foreground/40 hover:text-accent transition-colors duration-300 text-sm"
            >
              {t('cookies.settings')}
            </button>
            <button
              onClick={scrollToTop}
              className="w-10 h-10 rounded-xl bg-primary-foreground/10 flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-all duration-300"
              aria-label={t('buttons.backToTop')}
            >
              <ArrowUp className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
